import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, getUserCreationAuth } from '../firebase/config';
import { validateUserLimit } from './subscriptionService';
import { sendUserCredentialsEmail } from './emailService';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Tech' | 'Clerk';
  assignedSites: string[];
  masterOwnerID: string;
  ownerID: string;
  lastLogin?: any;
  expoPushToken?: string;
  notificationTimeZone?: string;
  announcements?: boolean;
  companyRadius?: number;
  defaultSweepTeamNumber?: number | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface CreateUserResult {
  success: boolean;
  userId?: string;
  password?: string;
  error?: any;
  /** False if SendGrid/Pages function failed after user was created */
  emailSent?: boolean;
  emailError?: string;
}

// Generate a secure random password
export const generateSecurePassword = (): string => {
  const length = 12;
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';

  // Ensure at least one of each type
  password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
  password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
  password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
  password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special char

  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }

  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

// Enhanced user creation with Firebase Auth integration
export const createUserWithAuth = async (
  userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>,
  opts?: { password?: string }
): Promise<CreateUserResult> => {
  try {
    // Validate user limit before creating
    const validation = await validateUserLimit(userData.masterOwnerID, userData.masterOwnerID);
    
    if (!validation.canCreateUser) {
      return {
        success: false,
        error: validation.errorMessage || 'User limit exceeded'
      };
    }

    const provided = String(opts?.password ?? '').trim();
    const password =
      provided.length >= 8 ? provided : generateSecurePassword();

    // Use a secondary FirebaseApp Auth instance so the portal admin stays signed in.
    // createUserWithEmailAndPassword on the default auth replaces the current session.
    const creationAuth = getUserCreationAuth();
    let newUid: string;
    try {
      console.log('[userService] Creating Firebase Auth user for:', userData.email);
      const userCredential = await createUserWithEmailAndPassword(
        creationAuth,
        userData.email,
        password
      );
      newUid = userCredential.user.uid;
      console.log('[userService] Firebase Auth user created with UID:', newUid);

      // Create Firestore user document with user.uid as document ID (matching mobile app behavior)
      const userDoc = {
        ...userData,
        ownerID: newUid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      console.log('[userService] Creating Firestore document at users/' + newUid, userDoc);
      await setDoc(doc(db, 'users', newUid), userDoc);
      console.log('[userService] Firestore document created successfully');
    } finally {
      await signOut(creationAuth).catch(() => {
        /* ignore */
      });
    }

    // Fetch site details for assigned sites (using same method as SitesTab)
    let siteDetails: Array<{
      id: string;
      name: string;
      address: string;
      photo?: string;
    }> = [];
    if (userData.assignedSites && userData.assignedSites.length > 0) {
      try {
        // Fetch all sites for the company (same as SitesTab does)
        const sitesCollection = collection(db, 'sites');
        const q = query(
          sitesCollection,
          where('masterOwnerID', '==', userData.masterOwnerID)
        );
        const sitesSnapshot = await getDocs(q);
        
        // Filter to only the assigned sites and extract needed data
        sitesSnapshot.docs.forEach((siteDoc) => {
          const siteData = siteDoc.data();
          const siteId = siteDoc.id;
          
          // Only include sites that are assigned to this user
          if (userData.assignedSites.includes(siteId)) {
            siteDetails.push({
              id: siteId,
              name: siteData.name || 'Unknown Site',
              address: siteData.address || 'No address provided',
              photo: siteData.photo || undefined
            });
          }
        });
        
        console.log('Site details fetched for email:', siteDetails);
      } catch (error) {
        console.warn('Failed to fetch site details for email:', error);
      }
    }

    let emailSent = false;
    let emailError: string | undefined;
    try {
      await sendUserCredentialsEmail({
        name: userData.name,
        email: userData.email,
        password,
        role: userData.role,
        company: 'SkimBuster',
        assignedSites: siteDetails,
      });
      emailSent = true;
    } catch (e) {
      emailError = e instanceof Error ? e.message : String(e);
      console.warn('User created, but credential email failed:', emailError);
    }

    return {
      success: true,
      userId: newUid,
      password: password,
      emailSent,
      emailError,
    };

  } catch (error: any) {
    console.error('Error creating user with auth:', error);

    // Handle specific Firebase Auth errors
    let errorMessage = 'Failed to create user account';

    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'An account with this email already exists';
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'Password is too weak';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

// Create a new user
export const createUser = async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    const docRef = await addDoc(collection(db, 'users'), {
      ...userData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return { id: docRef.id, success: true };
  } catch (error) {
    console.error('Error creating user:', error);
    return { success: false, error };
  }
};

// Get all users for a company
export const getUsersByCompany = async (masterOwnerID: string) => {
  try {
    const q = query(
      collection(db, 'users'),
      where('masterOwnerID', '==', masterOwnerID)
    );
    const querySnapshot = await getDocs(q);
    const users: User[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const user: User = {
        id: doc.id,
        name: data.name || '',
        email: data.email || '',
        role: data.role || 'Clerk',
        assignedSites: data.assignedSites || [],
        masterOwnerID: data.masterOwnerID || '',
        ownerID: data.ownerID || '',
        lastLogin: data.lastLogin,
        expoPushToken: data.expoPushToken,
        notificationTimeZone: data.notificationTimeZone,
        announcements: data.announcements,
        companyRadius: data.companyRadius,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      };
      users.push(user);
    });
    return { success: true, users };
  } catch (error) {
    console.error('Error fetching users:', error);
    return { success: false, error, users: [] };
  }
};

// Get users assigned to a specific site
export const getUsersBySite = async (siteId: string) => {
  try {
    const q = query(
      collection(db, 'users'),
      where('assignedSites', 'array-contains', siteId),
      orderBy('name')
    );
    const querySnapshot = await getDocs(q);
    const users: User[] = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data() as Omit<User, 'id'>;
      // Remove assignedSites from response for cleaner data
      const { assignedSites, ...cleanUserData } = userData;
      users.push({ id: doc.id, assignedSites: assignedSites || [], ...cleanUserData });
    });
    return { success: true, users };
  } catch (error) {
    console.error('Error fetching users by site:', error);
    return { success: false, error, users: [] };
  }
};

// Update user profile
export const updateUser = async (userId: string, updateData: Partial<Omit<User, 'id' | 'createdAt'>>) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...updateData,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user:', error);
    return { success: false, error };
  }
};

// Delete a user from Firestore only (legacy function)
export const deleteUser = async (userId: string) => {
  try {
    await deleteDoc(doc(db, 'users', userId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting user:', error);
    return { success: false, error };
  }
};

// Enhanced user deletion with Firebase Auth integration
export const deleteUserWithAuth = async (userId: string, userEmail: string) => {
  try {
    // First, delete from Firestore
    await deleteDoc(doc(db, 'users', userId));

    // Attempt to delete from Firebase Auth
    // This will only work if the current user is the one being deleted
    // or if using Firebase Admin SDK
    try {
      // Get user by email to get their UID
      // Note: This is a simplified approach. In production, you'd use Admin SDK
      console.log(`Attempting to delete Firebase Auth account for: ${userEmail}`);

      // For now, we'll document that manual deletion is required
      // In a production app, you'd use Firebase Cloud Functions with Admin SDK
      console.warn(`⚠️ Firebase Auth deletion requires manual action in Firebase Console`);
      console.warn(`📧 Email to delete: ${userEmail}`);
      console.warn(`🔗 Firebase Console: https://console.firebase.google.com`);

    } catch (authError: any) {
      console.warn(`Firebase Auth deletion failed: ${authError.message}`);
      console.warn(`Manual deletion required in Firebase Console for: ${userEmail}`);
    }

    return {
      success: true,
      message: `User ${userEmail} deleted from database. Firebase Auth account requires manual deletion.`
    };

  } catch (error: any) {
    console.error('Error deleting user:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete user'
    };
  }
};

// Get user by ID
export const getUserById = async (userId: string) => {
  try {
    const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
    if (!userDoc.empty) {
      const doc = userDoc.docs[0];
      const data = doc.data();
      return { success: true, user: { id: doc.id, ...data } as User };
    }
    return { success: false, error: 'User not found' };
  } catch (error) {
    console.error('Error fetching user:', error);
    return { success: false, error };
  }
};

// Get users by role
export const getUsersByRole = async (masterOwnerID: string, role: 'Admin' | 'Tech' | 'Clerk') => {
  try {
    const q = query(
      collection(db, 'users'),
      where('masterOwnerID', '==', masterOwnerID),
      where('role', '==', role),
      orderBy('name')
    );
    const querySnapshot = await getDocs(q);
    const users: User[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      users.push({
        id: doc.id,
        name: data.name || '',
        email: data.email || '',
        role: data.role || 'Clerk',
        assignedSites: data.assignedSites || [],
        masterOwnerID: data.masterOwnerID || '',
        ownerID: data.ownerID,
        companyRadius: data.companyRadius,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      });
    });
    return { success: true, users };
  } catch (error) {
    console.error('Error fetching users by role:', error);
    return { success: false, error, users: [] };
  }
};

// Search users by name or email
export const searchUsers = async (masterOwnerID: string, searchTerm: string) => {
  try {
    const q = query(
      collection(db, 'users'),
      where('masterOwnerID', '==', masterOwnerID),
      orderBy('name'),
      limit(50) // Limit results for performance
    );
    const querySnapshot = await getDocs(q);
    const users: User[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const userData = {
        name: data.name || '',
        email: data.email || '',
        role: data.role || 'Clerk',
        assignedSites: data.assignedSites || [],
        masterOwnerID: data.masterOwnerID || '',
        ownerID: data.ownerID,
        companyRadius: data.companyRadius,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      };
      // Simple search in name and email
      if (
        userData.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        userData.email.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        users.push({ id: doc.id, ...userData });
      }
    });
    return { success: true, users };
  } catch (error) {
    console.error('Error searching users:', error);
    return { success: false, error, users: [] };
  }
};

// Bulk update user roles
export const bulkUpdateUserRoles = async (userIds: string[], newRole: 'Admin' | 'Clerk') => {
  try {
    const updatePromises = userIds.map(userId =>
      updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: Timestamp.now(),
      })
    );
    await Promise.all(updatePromises);
    return { success: true };
  } catch (error) {
    console.error('Error bulk updating user roles:', error);
    return { success: false, error };
  }
};

// Get user count for a company
export const getUserCount = async (masterOwnerID: string) => {
  try {
    const q = query(collection(db, 'users'), where('masterOwnerID', '==', masterOwnerID));
    const querySnapshot = await getDocs(q);
    return { success: true, count: querySnapshot.size };
  } catch (error) {
    console.error('Error getting user count:', error);
    return { success: false, error, count: 0 };
  }
};
